/**
 * Homepage vertical slider (fullpage, miumiu-style).
 *
 * Native scroll is disabled and each wheel / swipe / arrow-key gesture advances ONE full-screen slide
 * with a cover-reveal transition: the incoming slide slides up over the current one (and back down on
 * reverse). Slides = the index template's sections + the footer.
 *
 * Falls back to a normal stacked scroll when JS can't take over — in the Theme Editor (so sections stay
 * editable) or before the controller flags <html> with `.jd-fullpage`. Numbering ("N / total") is added
 * in both modes. Reduced motion keeps the paging but drops the animation (see the CSS).
 */
(function () {
  const SELECTOR_MAIN = 'main[data-template="index"]';
  const DURATION = 900; // keep in sync with --jd-slide-dur in the CSS
  const COOLDOWN = 120; // extra lock after the transition, to swallow trackpad momentum
  const WHEEL_THRESHOLD = 8;
  const SWIPE_THRESHOLD = 40;

  const main = document.querySelector(SELECTOR_MAIN);
  const inDesignMode = !!(window.Shopify && window.Shopify.designMode);

  /* ----- Numbering (both modes) ----- */
  const numberSlides = function () {
    if (!main) return;
    main.querySelectorAll('.jd-section-index').forEach((b) => b.remove());
    main.querySelectorAll('[data-jd-scroll-anchor]').forEach((a) => (a.style.display = ''));

    const sections = Array.from(main.children).filter((el) => el.classList.contains('shopify-section'));
    const total = sections.length;
    if (total < 2) return;

    sections.forEach((section, index) => {
      if (index === 0) return; // first slide isn't numbered
      const anchor = section.querySelector('[data-jd-scroll-anchor]');
      if (anchor) anchor.style.display = 'none'; // avoid a duplicate tick

      const badge = document.createElement('div');
      badge.className = 'jd-section-index';
      const label = document.createElement('span');
      label.textContent = index + 1 + ' / ' + total;
      badge.appendChild(label);
      if (index < total - 1) {
        const line = document.createElement('span');
        line.className = 'jd-section-index__line';
        badge.appendChild(line);
      }
      section.appendChild(badge);
    });
  };

  /* ----- Fullpage controller ----- */
  const setupFullpage = function () {
    if (!main || inDesignMode) return;

    const slides = Array.from(main.children).filter((el) => el.classList.contains('shopify-section'));
    const footer = document.querySelector('body > footer');
    if (footer) slides.push(footer);
    if (slides.length < 2) return;

    document.documentElement.classList.add('jd-fullpage');

    let current = 0;
    let locked = false;

    const layout = function (animate) {
      slides.forEach((slide, i) => {
        // Slides inside main are isolated from the rest of the page, so their counter is free. The
        // footer is outside it and its layer is owned by the CSS, which keeps it under the header.
        if (slide !== footer) slide.style.zIndex = String(i);
        if (!animate) slide.style.transition = 'none';
        slide.style.transform = i <= current ? 'translateY(0)' : 'translateY(100%)';
      });
      if (!animate) {
        // Force reflow, then restore the transition so the next move animates.
        void main.offsetHeight;
        slides.forEach((slide) => (slide.style.transition = ''));
      }
    };

    const goTo = function (index) {
      const target = Math.max(0, Math.min(slides.length - 1, index));
      if (target === current || locked) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      current = target;
      locked = true;
      layout(true);
      window.setTimeout(() => (locked = false), (reduced ? 0 : DURATION) + COOLDOWN);
    };

    const next = () => goTo(current + 1);
    const prev = () => goTo(current - 1);

    // Ignore paging while an overlay owns the viewport (cart/menu/search drawer, any modal dialog).
    const overlayOpen = function () {
      if (document.querySelector('dialog[open]')) return true;
      return document.documentElement.classList.contains('scroll-lock') || document.body.classList.contains('scroll-lock');
    };

    // Gesture hosts. The footer is a slide too, but it hangs off <body> rather than main: once it
    // covers the viewport, a wheel/swipe over it never reaches main, leaving no way back up. Binding
    // both (instead of the document) keeps the gestures off the header's drawers, which scroll natively.
    const gestureHosts = footer ? [main, footer] : [main];

    // A slide can hold a natively scrollable region (the footer's country list). Paging swallows the
    // gesture, so hand it back while such a region still has room in the direction being scrolled.
    // Positive delta = scrolling down, for both wheel and swipe.
    const scrollsNatively = function (target, host, deltaY) {
      for (let el = target instanceof HTMLElement ? target : null; el && el !== host; el = el.parentElement) {
        if (el.scrollHeight <= el.clientHeight) continue;
        if (!/auto|scroll/.test(window.getComputedStyle(el).overflowY)) continue;
        return deltaY < 0 ? el.scrollTop > 0 : Math.ceil(el.scrollTop + el.clientHeight) < el.scrollHeight;
      }
      return false;
    };

    /* Same test on the other axis. The homepage is full of horizontal tracks — the reassurance row,
       the product showcase, the multicolumn carousel — and a swipe across one of them has to reach it
       rather than be swallowed. Until this existed every horizontal gesture was preventDefault()-ed,
       since the vertical test above can never recognise one. */
    const scrollsHorizontally = function (target, host, deltaX) {
      for (let el = target instanceof HTMLElement ? target : null; el && el !== host; el = el.parentElement) {
        if (el.scrollWidth <= el.clientWidth) continue;
        if (!/auto|scroll/.test(window.getComputedStyle(el).overflowX)) continue;
        return deltaX < 0 ? el.scrollLeft > 0 : Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth;
      }
      return false;
    };

    // Wheel
    gestureHosts.forEach((host) =>
      host.addEventListener(
        'wheel',
        (event) => {
          if (scrollsNatively(event.target, host, event.deltaY)) return;
          if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && scrollsHorizontally(event.target, host, event.deltaX)) {
            return;
          }
          event.preventDefault();
          if (locked || overlayOpen()) return;
          if (Math.abs(event.deltaY) < WHEEL_THRESHOLD) return;
          if (event.deltaY > 0) next();
          else prev();
        },
        { passive: false }
      )
    );

    // Keyboard
    document.addEventListener('keydown', (event) => {
      if (locked || overlayOpen()) return;
      if (event.target instanceof HTMLElement && event.target.closest('input, textarea, select, [contenteditable]')) return;
      switch (event.key) {
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
          event.preventDefault();
          next();
          break;
        case 'ArrowUp':
        case 'PageUp':
          event.preventDefault();
          prev();
          break;
        case 'Home':
          event.preventDefault();
          goTo(0);
          break;
        case 'End':
          event.preventDefault();
          goTo(slides.length - 1);
          break;
      }
    });

    // Touch
    let touchStartY = null;
    let touchStartX = null;
    let touchScrolled = false; // the swipe is scrolling a region inside the slide, not paging
    gestureHosts.forEach((host) => {
      host.addEventListener(
        'touchstart',
        (event) => {
          touchStartY = event.touches[0].clientY;
          touchStartX = event.touches[0].clientX;
          touchScrolled = false;
        },
        { passive: true }
      );
      host.addEventListener(
        'touchmove',
        (event) => {
          if (touchStartY === null) {
            event.preventDefault();
            return;
          }

          const deltaY = touchStartY - event.touches[0].clientY;
          const deltaX = touchStartX - event.touches[0].clientX;

          if (scrollsNatively(event.target, host, deltaY)) {
            touchScrolled = true;
            return;
          }
          /* Dominant axis decides: a mostly-vertical drag still pages the slider even when it starts
             over a carousel, so the visitor is never trapped on a slide. */
          if (Math.abs(deltaX) > Math.abs(deltaY) && scrollsHorizontally(event.target, host, deltaX)) {
            touchScrolled = true;
            return;
          }
          event.preventDefault();
        },
        { passive: false }
      );
      host.addEventListener(
        'touchend',
        (event) => {
          if (touchStartY === null || touchScrolled || locked || overlayOpen()) return;
          const delta = touchStartY - event.changedTouches[0].clientY;
          touchStartY = null;
          if (Math.abs(delta) < SWIPE_THRESHOLD) return;
          if (delta > 0) next();
          else prev();
        },
        { passive: true }
      );
    });

    // A section link ("Découvrir"/scroll tick) advances one slide instead of jumping.
    main.addEventListener('click', (event) => {
      const anchor = event.target instanceof HTMLElement ? event.target.closest('[data-jd-scroll-anchor]') : null;
      if (anchor) {
        event.preventDefault();
        next();
      }
    });

    /* A form posted from a slide comes back as `<page>#<form-id>` (the footer newsletter does, so the
       visitor sees its confirmation). Native fragment navigation is powerless here: the slides are
       fixed and stacked with transforms, and fullpage mode leaves nothing to scroll. Open on the
       slide holding the target instead — through `layout(false)`, so it is already there on the
       first paint rather than travelling to it. */
    const slideOfFragment = function () {
      const raw = window.location.hash.slice(1);
      if (!raw) return 0;
      let id = raw;
      // A malformed escape (`#%`) makes decodeURIComponent throw; the raw hash is the better guess
      // than losing the controller, which by now has already flagged <html> as fullpage.
      try {
        id = decodeURIComponent(raw);
      } catch (e) {
        /* keep the raw hash */
      }
      const target = document.getElementById(id);
      if (!target) return 0;
      const index = slides.findIndex((slide) => slide.contains(target));
      return index > 0 ? index : 0;
    };

    current = slideOfFragment();
    layout(false);
  };

  numberSlides();
  setupFullpage();

  // Keep numbering in sync while editing (fullpage stays off in design mode).
  if (inDesignMode) {
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((name) =>
      document.addEventListener(name, numberSlides)
    );
  }
})();
